import React from 'react';
import {
  Box, Collapse, Flex, SimpleGrid, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiUser } from 'react-icons/fi';
import GlassCard from './GlassCard';
import AuroraBackground from './dashboard/AuroraBackground';
import PageHeader from './PageHeader';
import { ContactInfo } from '../models/contactInfo';

// item 485 (bug-drops #2) — CRM info ON the project view: the customer's contact details +
// their staff roster, read-only, right where the work happens. Data rides the project GET
// (customer + staff are included server-side); the CRM record stays the edit surface.
const ProjectCustomerCard: React.FC<{
  customer?: ContactInfo;
  collapse?: { collapsed: boolean; onToggle: () => void };
}> = ({ customer, collapse }) => {
  const subColor = useColorModeValue('gray.500', 'whiteAlpha.600');
  const headBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const collapsed = collapse?.collapsed ?? false;

  if (!customer) return null;
  const staff = customer.staff ?? [];
  const addr = customer.serviceAddress;
  const addressLine = addr
    ? [addr.street, addr.unit, addr.city, addr.state, addr.zip].filter(Boolean).join(', ')
    : '';

  const info: { label: string; value?: string }[] = [
    { label: 'Phone', value: customer.phone },
    { label: 'Email', value: customer.email },
    { label: 'Accounting email', value: (customer as any).accountingEmail },
    { label: 'Address', value: addressLine },
    { label: 'Website', value: (customer as any).url },
  ].filter((f) => !!f.value);

  return (
    <Box
      position="relative"
      mx={{ base: -4, md: -10 }}
      px={{ base: 4, md: 10 }}
      pt={collapsed ? 1 : { base: 2, md: 3 }}
      pb={collapsed ? 1 : { base: 2, md: 3 }}
    >
      <AuroraBackground />
      <Box position="relative" zIndex={1}>
        <PageHeader
          dense
          onTitleClick={collapse?.onToggle}
          collapsed={collapsed}
          icon={<FiUser />}
          title="Customer"
          subtitle={customer.name}
        />
        <Collapse in={!collapsed} animateOpacity>
          <GlassCard p={5}>
            {info.length > 0 && (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3} mb={staff.length ? 5 : 0}>
                {info.map((f) => (
                  <Box key={f.label}>
                    <Text fontSize="0.62rem" letterSpacing="0.08em" textTransform="uppercase" color={subColor}>{f.label}</Text>
                    <Text fontSize="sm">{f.value}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            )}
            {staff.length > 0 && (
              <TableContainer>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr bg={headBg}>
                      <Th>STAFF</Th>
                      <Th>ROLE</Th>
                      <Th>PHONE</Th>
                      <Th>EMAIL</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {staff.map((s) => (
                      <Tr key={s.id ?? s.name}>
                        <Td>{s.name}</Td>
                        <Td>{s.role ?? '—'}</Td>
                        <Td>{s.phone ?? '—'}</Td>
                        <Td>{s.email ?? '—'}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
            {info.length === 0 && staff.length === 0 && (
              <Flex><Text fontSize="sm" color={subColor}>No contact details on the CRM record yet.</Text></Flex>
            )}
          </GlassCard>
        </Collapse>
      </Box>
    </Box>
  );
};

ProjectCustomerCard.defaultProps = { customer: undefined, collapse: undefined };

export default ProjectCustomerCard;
